import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Headphones, Ticket, MapPin, ArrowLeft, Search, SlidersHorizontal, X, Loader2, Zap } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { formatCurrency } from '../lib/utils';
import { SEO, CitySchema, BreadcrumbSchema } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CityDetailsPage() {
    const { citySlug } = useParams();
    const brand = useBrand();
    const [city, setCity] = useState(null);
    const [attractions, setAttractions] = useState([]);
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [priceSort, setPriceSort] = useState('none');
    const [showFilters, setShowFilters] = useState(false);
    const [blogPosts, setBlogPosts] = useState([]);

    useEffect(() => { fetchCityData(); }, [citySlug]);

    const fetchCityData = async () => {
        setLoading(true);
        try {
            const cityRes = await fetch(`${API}/api/public/cities/slug/${citySlug}`);
            if (!cityRes.ok) { setLoading(false); return; }
            const cityData = await cityRes.json();
            setCity(cityData);

            const [attractionsRes, listingsRes, blogRes] = await Promise.all([
                fetch(`${API}/api/public/cities/${cityData.id}/attractions`).then(r => r.json()).catch(() => []),
                fetch(`${API}/api/public/listings?city_id=${cityData.id}`).then(r => r.json()).catch(() => []),
                fetch(`${API}/api/blog/posts?city_id=${cityData.id}&limit=3`).then(r => r.json()).catch(() => ({ posts: [] }))
            ]);
            setAttractions(Array.isArray(attractionsRes) ? attractionsRes : []);
            setListings(Array.isArray(listingsRes) ? listingsRes : []);
            setBlogPosts(blogRes.posts || []);
        } catch {}
        finally { setLoading(false); }
    };

    const getLowestPrice = (listing) => {
        const opts = listing.options || [];
        if (opts.length === 0) return 0;
        return Math.min(...opts.map(o => o.pricing?.adult || 0));
    };

    const getImageUrl = (img) => {
        if (!img) return null;
        if (img.startsWith('http')) return img;
        return `${API}${img}`;
    };

    // Gather unique categories from attractions + listings
    const allCategories = [...new Set([
        ...attractions.map(a => a.category),
        ...listings.map(l => l.category)
    ])].filter(Boolean);

    // Filter & sort listings
    const filteredListings = listings.filter(l => {
        if (search && !l.title.toLowerCase().includes(search.toLowerCase())) return false;
        if (categoryFilter !== 'all' && l.category !== categoryFilter) return false;
        return true;
    }).sort((a, b) => {
        if (priceSort === 'low') return getLowestPrice(a) - getLowestPrice(b);
        if (priceSort === 'high') return getLowestPrice(b) - getLowestPrice(a);
        return (a.sort_order || 0) - (b.sort_order || 0);
    });

    // Filter attractions
    const filteredAttractions = attractions.filter(a => {
        if (search && !a.title.toLowerCase().includes(search.toLowerCase())) return false;
        if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
        return true;
    });

    const hasActiveFilters = search || categoryFilter !== 'all' || priceSort !== 'none';

    if (loading) return (
        <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
    );

    if (!city) return (
        <div className="min-h-screen bg-[#0A0D14] flex flex-col items-center justify-center gap-4">
            <p className="text-white text-lg">City not found</p>
            <Link to="/"><Button variant="outline" className="border-white/10 text-white gap-2"><ArrowLeft className="w-4 h-4" /> Back to Home</Button></Link>
        </div>
    );

    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <div className="min-h-screen bg-[#0A0D14]">
            <SEO
                title={`${city.name}, ${city.country} — Audio Guides & Attractions`}
                description={city.description || `Explore ${city.name}, ${city.country} with premium self-guided audio tours and skip-the-line tickets. ${attractions.length} attraction${attractions.length !== 1 ? 's' : ''} available.`}
                image={city.image_url}
                url={pageUrl}
            />
            <CitySchema city={city} url={pageUrl} />
            <BreadcrumbSchema items={[{ name: 'Home', url: `${window.location.origin}/` }, { name: city.name }]} />
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 sm:gap-3">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-7 sm:h-9 max-w-[120px] sm:max-w-[160px] object-contain" />
                        ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-gold flex items-center justify-center">
                                <Headphones className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A0D14]" />
                            </div>
                        )}
                        {!brand.logo_url && <span className="font-outfit font-semibold text-base sm:text-xl text-white">{brand.platform_name}</span>}
                    </Link>
                    <Link to="/"><Button variant="ghost" className="text-white/60 hover:text-white gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Back</Button></Link>
                </div>
            </header>

            {/* Hero Banner */}
            <section className="relative pt-16 h-[320px] sm:h-[400px] overflow-hidden">
                {city.image_url && (
                    <img src={city.image_url} alt={city.name} className="absolute inset-0 w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/60 to-[#0A0D14]/30" />
                <div className="relative h-full flex flex-col justify-end pb-8 sm:pb-12 px-4 sm:px-6">
                    <div className="max-w-7xl mx-auto w-full">
                        <div className="flex items-center gap-2 text-gold text-xs sm:text-sm mb-2">
                            <MapPin className="w-3.5 h-3.5" /> {city.country}
                        </div>
                        <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2" data-testid="city-name">{city.name}</h1>
                        <p className="text-muted-foreground text-sm sm:text-base max-w-2xl">{city.description}</p>
                        <div className="flex items-center gap-4 mt-4 text-sm text-white/60">
                            {attractions.length > 0 && <span>{attractions.length} attraction{attractions.length !== 1 ? 's' : ''}</span>}
                            {listings.length > 0 && <span>{listings.length} experience{listings.length !== 1 ? 's' : ''}</span>}
                        </div>
                    </div>
                </div>
            </section>

            {/* Filters Bar */}
            <section className="sticky top-[56px] sm:top-[64px] z-40 bg-[#0A0D14]/95 backdrop-blur-sm border-b border-white/5 py-3 px-4 sm:px-6">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search experiences..." className="bg-white/5 border-white/10 text-white pl-9 h-9" data-testid="city-search" />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className={`border-white/10 text-white/70 gap-1.5 h-9 ${showFilters ? 'bg-white/10' : ''}`} data-testid="filter-toggle">
                        <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setCategoryFilter('all'); setPriceSort('none'); }} className="text-gold text-xs gap-1 h-9"><X className="w-3 h-3" /> Clear</Button>
                    )}
                </div>
                {showFilters && (
                    <div className="max-w-7xl mx-auto flex flex-wrap gap-3 mt-3">
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white w-40 h-9 text-xs" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={priceSort} onValueChange={setPriceSort}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white w-40 h-9 text-xs" data-testid="price-sort"><SelectValue placeholder="Price" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Default Order</SelectItem>
                                <SelectItem value="low">Price: Low to High</SelectItem>
                                <SelectItem value="high">Price: High to Low</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </section>

            {/* Experiences / Listings */}
            {filteredListings.length > 0 && (
                <section className="py-10 sm:py-14 px-4 sm:px-6" data-testid="city-listings">
                    <div className="max-w-7xl mx-auto">
                        <p className="text-gold uppercase tracking-widest text-xs mb-2">Book Now</p>
                        <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-white mb-8">Experiences in {city.name}</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredListings.map(listing => (
                                <button key={listing.id} onClick={() => window.location.href = `/experience/${listing.id}`}
                                    className="group text-left rounded-xl overflow-hidden border border-white/10 bg-[#121620] card-hover transition-all" data-testid={`listing-card-${listing.id}`}>
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        {listing.featured_image ? (
                                            <img src={getImageUrl(listing.featured_image)} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center"><Headphones className="w-10 h-10 text-gold/40" /></div>
                                        )}
                                        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                                            {(listing.options || []).slice(0, 2).map((opt, idx) => {
                                                const isCombo = opt.has_tickets || (opt.pricing?.adult > 0 && idx > 0);
                                                return (
                                                    <span key={opt.id} className={`px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm flex items-center gap-1 ${isCombo ? 'bg-amber-500/90 text-[#0A0D14]' : 'bg-emerald-500/90 text-white'}`}>
                                                        {isCombo ? <Ticket className="w-3 h-3" /> : <Headphones className="w-3 h-3" />} {opt.name.length > 18 ? opt.name.substring(0, 18) + '...' : opt.name}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        {listing.category && <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs bg-[#0A0D14]/70 text-white/80 backdrop-blur-sm">{listing.category}</span>}
                                    </div>
                                    <div className="p-4 sm:p-5">
                                        <h3 className="font-outfit font-semibold text-white mb-1 group-hover:text-gold transition-colors">{listing.title}</h3>
                                        {listing.instant_confirmation && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-medium border border-emerald-500/20 mb-1">
                                                <Zap className="w-2.5 h-2.5" />Instant Confirmation
                                            </span>
                                        )}
                                        <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{listing.short_description || listing.description}</p>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-muted-foreground">From</p>
                                                <p className="text-lg font-outfit font-bold text-white">{formatCurrency(getLowestPrice(listing), listing.currency?.toUpperCase() || 'EUR')}</p>
                                            </div>
                                            <span className="px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-medium group-hover:bg-gold group-hover:text-[#0A0D14] transition-all">View Details</span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Audio Tours / Attractions */}
            {filteredAttractions.length > 0 && (
                <section className="py-10 sm:py-14 px-4 sm:px-6 border-t border-white/5" data-testid="city-attractions">
                    <div className="max-w-7xl mx-auto">
                        <p className="text-gold uppercase tracking-widest text-xs mb-2">Audio Tours</p>
                        <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-white mb-8">Attractions in {city.name}</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredAttractions.map(attraction => (
                                <div key={attraction.id} className="group rounded-xl overflow-hidden border border-white/10 bg-[#121620] card-hover" data-testid={`attraction-card-${attraction.id}`}>
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        {attraction.featured_image ? (
                                            <img src={getImageUrl(attraction.featured_image)} alt={attraction.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center"><Headphones className="w-10 h-10 text-blue-400/40" /></div>
                                        )}
                                        <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs bg-[#0A0D14]/70 text-white/80 backdrop-blur-sm">{attraction.category}</span>
                                    </div>
                                    <div className="p-4 sm:p-5">
                                        <h3 className="font-outfit font-semibold text-white mb-1 group-hover:text-gold transition-colors">{attraction.title}</h3>
                                        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{attraction.description}</p>
                                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                            {attraction.audio_duration && <span className="flex items-center gap-1"><Headphones className="w-3 h-3" /> {attraction.audio_duration} min</span>}
                                            {attraction.languages?.length > 0 && <span>{attraction.languages.length} languages</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Blog Posts for this City */}
            {blogPosts.length > 0 && (
                <section className="py-10 sm:py-14 px-4 sm:px-6 border-t border-white/5">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <p className="text-gold uppercase tracking-widest text-xs mb-2">Travel Insider</p>
                                <h2 className="font-outfit text-xl sm:text-2xl font-bold text-white">{city.name} Guides & Tips</h2>
                            </div>
                            <Link to="/blog" className="text-gold text-xs hover:text-gold/80">View all articles</Link>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-5">
                            {blogPosts.map(post => (
                                <Link key={post.slug} to={`/blog/${post.slug}`}
                                    className="group bg-[#121620] border border-white/10 rounded-xl overflow-hidden hover:border-gold/20 transition-all">
                                    {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-500" />}
                                    <div className="p-4">
                                        <span className="text-[10px] text-gold bg-gold/10 px-2 py-0.5 rounded-full">{post.category}</span>
                                        <h3 className="text-sm font-medium text-white mt-2 group-hover:text-gold transition-colors line-clamp-2">{post.title}</h3>
                                        <p className="text-xs text-white/30 mt-1">{post.reading_time || 1} min read</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Empty state */}
            {filteredListings.length === 0 && filteredAttractions.length === 0 && (
                <div className="py-20 text-center px-4">
                    <p className="text-muted-foreground text-lg mb-2">No results found</p>
                    <p className="text-muted-foreground text-sm">Try adjusting your filters or search term</p>
                </div>
            )}
        </div>
    );
}
